import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to track user inactivity and trigger logout after specified time
 * @param {Function} onInactive - Callback function to execute when inactivity timeout is reached
 * @param {number} timeoutMinutes - Minutes of inactivity before timeout (default: 5)
 */
const useInactivityTimer = (onInactive, timeoutMinutes = 5) => {
  const timeoutRef = useRef(null);
  const onInactiveRef = useRef(onInactive);
  const timeoutMs = timeoutMinutes * 60 * 1000; // Convert minutes to milliseconds

  // Keep the latest callback reference
  useEffect(() => {
    onInactiveRef.current = onInactive;
  }, [onInactive]);

  const resetTimer = useCallback(() => {
    // Clear existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timer
    timeoutRef.current = setTimeout(() => {
      onInactiveRef.current();
    }, timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    // List of events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    // Add event listeners for user activity
    events.forEach((event) => {
      window.addEventListener(event, resetTimer, true);
    });

    // Initialize timer
    resetTimer();

    // Cleanup function
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimer]);
};

export default useInactivityTimer;

